import {
  AreaChartOutlined,
  EditOutlined,
  LaptopOutlined,
  RollbackOutlined,
  ScheduleOutlined,
  SettingOutlined,
  SolutionOutlined,
  TeamOutlined,
  TrophyOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { Divider, Popover } from 'antd'
import * as R from 'ramda'
import React from 'react'
import { Link } from 'react-router-dom'

import { DISPLAY_MODE_TV } from '../../constants/displayMode'
import { TenantId } from '../../schemas/Tenant'
import { Tournament, TournamentId } from '../../schemas/Tournament'

import routes from '../../helpers/routes'
import { DeleteLinkWithConfirmation } from '../ActionWithConfirmation'

import './TournamentMenu.css'

const createActionLink = ({ to, icon, linkText }) => (
  <Link key={to} to={to}>
    {icon} {linkText}
  </Link>
)

type DeleteTournamentProps = {
  removeTournamentAction(): void
}

const DeleteTournament: React.FunctionComponent<DeleteTournamentProps> = ({
  removeTournamentAction,
}) => (
  <DeleteLinkWithConfirmation
    title="Are you sure to delete this tournament?"
    popupPlacement="left"
    onConfirmAction={removeTournamentAction}
  />
)

const FlightsPerRound = ({ tenant, tournamentId, rounds, finalRound }) => {
  const content = (
    <div className="popover-actions">
      {R.range(1, rounds + 1).map((round) => (
        <Link
          key={round}
          to={routes.liveFlights(tenant, tournamentId, round)}>{`${round}. round`}</Link>
      ))}
      {finalRound && (
        <Link key="final" to={routes.liveFlights(tenant, tournamentId, rounds + 1)}>
          Final round
        </Link>
      )}
    </div>
  )

  return (
    <Popover content={content}>
      <Link to="#">
        <LaptopOutlined />
        <span>View Flights on TV</span>
      </Link>
    </Popover>
  )
}

const ViewResults = ({ tenant, tournamentId }) => {
  return (
    <Link to={routes.viewResults(tenant, tournamentId)}>
      <TrophyOutlined />
      <span>Results</span>
    </Link>
  )
}

const ViewTeamResults = ({ tenant, tournamentId }) => {
  return (
    <Link to={routes.viewTeamResults(tenant, tournamentId)}>
      <TeamOutlined />
      <span>Team Results</span>
    </Link>
  )
}

const ViewFlights = ({ tenant, tournamentId }) => (
  <Link to={routes.viewFlights(tenant, tournamentId)}>
    <ScheduleOutlined />
    <span>Flights</span>
  </Link>
)

const ViewTournamentStatistics = ({ tenant, tournamentId }) => {
  return (
    <Link to={routes.viewTournamentStatistics(tenant, tournamentId)}>
      <AreaChartOutlined />
      <span>Statistics</span>
    </Link>
  )
}

type ManageActionProps = {
  tenant: TenantId
  tournamentId: TournamentId
  tournament: Tournament
  teamsEnabled: boolean
  removeTournamentAction(): void
}

const ManageAction: React.FunctionComponent<ManageActionProps> = ({
  removeTournamentAction,
  tenant,
  tournamentId,
  tournament,
  teamsEnabled,
}) => {
  const content = (
    <div className="popover-actions">
      {[
        {
          to: routes.editTournament(tenant, tournamentId),
          icon: <EditOutlined />,
          linkText: 'Edit',
        },
        {
          to: routes.managePlayers(tenant, tournamentId),
          icon: <UserOutlined />,
          linkText: 'Players',
        },
        {
          to: routes.manageTournamentCategories(tenant, tournamentId),
          icon: <SolutionOutlined />,
          linkText: 'Categories',
        },
        {
          to: routes.manageFlights(tenant, tournamentId),
          icon: <ScheduleOutlined />,
          linkText: 'Flights',
        },
        {
          to: `${routes.viewResults(tenant, tournamentId)}/main?displayMode=${DISPLAY_MODE_TV}`,
          icon: <LaptopOutlined />,
          linkText: 'View Main Results on TV',
        },
      ]
        .concat(
          teamsEnabled
            ? [
                {
                  to: `${routes.viewTeamResults(
                    tenant,
                    tournamentId,
                  )}?displayMode=${DISPLAY_MODE_TV}`,
                  icon: <TeamOutlined />,
                  linkText: 'View Team Results on TV ',
                },
              ]
            : [],
        )
        .concat([
          {
            to: `${routes.viewResults(tenant, tournamentId)}/others?displayMode=${DISPLAY_MODE_TV}`,
            icon: <LaptopOutlined />,
            linkText: 'View Other Results on TV',
          },
        ])
        .map((item) => createActionLink(item))}
      <FlightsPerRound
        key="live"
        tenant={tenant}
        tournamentId={tournamentId}
        rounds={tournament.rounds}
        finalRound={tournament.finalRound}
      />
      <DeleteTournament removeTournamentAction={removeTournamentAction} />
    </div>
  )

  return (
    <Popover content={content} trigger="click">
      <Link to="#">
        <SettingOutlined />
        Manage
      </Link>
    </Popover>
  )
}

type TournamentMenuProps = {
  admin: boolean
  tenant: TournamentId
  tourney: Tournament
  removeTournament(id: string): void
}

// this is required because of AntDesign List.Item `actions` API
export const renderMenuInList = ({
  admin,
  tenant,
  tourney,
  removeTournament,
  showBack = false,
}) => {
  const publicActions = [
    showBack && (
      <Link key="home" to="/">
        <RollbackOutlined /> Home
      </Link>
    ),
    <ViewResults key="viewResults" tenant={tenant} tournamentId={tourney.id} />,
  ]
    .concat(
      tourney.teamsEnabled
        ? [<ViewTeamResults key="viewTeamResults" tenant={tenant} tournamentId={tourney.id} />]
        : [],
    )
    .concat([
      <ViewFlights key="viewFlights" tenant={tenant} tournamentId={tourney.id} />,
      <ViewTournamentStatistics
        key="viewTournamentStatistics"
        tenant={tenant}
        tournamentId={tourney.id}
      />,
    ])
    .filter(Boolean)

  return admin
    ? [
        <ManageAction
          key="manage"
          tournament={tourney}
          tenant={tenant}
          tournamentId={tourney.id}
          removeTournamentAction={removeTournament(tourney.id)}
          teamsEnabled={tourney.teamsEnabled}
        />,
        ...publicActions,
      ]
    : publicActions
}

const TournamentMenu = ({ admin, tenant, tourney, removeTournament }: TournamentMenuProps) => {
  return (
    <div className="TournamentMenu">
      <div>{renderMenuInList({ admin, tenant, tourney, removeTournament, showBack: true })}</div>
      <Divider />
    </div>
  )
}

export default TournamentMenu



// WEBPACK FOOTER //
// ./src/components/tournament/TournamentMenu.tsx